import { useState, useEffect } from "react";
import { API_ENDPOINTS } from "../../../constants/constants";
import {
  RefreshCw,
  Search,
  Edit,
  Trash2,
  Eye,
  X,
  ArrowLeft,
  File,
  AlertTriangle,
  Upload,
} from "lucide-react";

// Interface pour les modules
interface ResourceModule {
  id: string;
  title: string;
  description: string;
  createdAt: string;
  isActive: boolean;
  order?: number;
  content?: string;
}

// Interface pour les ressources pédagogiques
interface Resource {
  id: string;
  title: string;
  description: string;
  fileUrl: string;
  fileSize?: number;
  createdAt: string;
  moduleId: number;
  order?: number;
}

// Interface pour le formulaire de création de module
interface CreateModuleForm {
  title: string;
  description: string;
  order: number;
  content: string;
}

// Interface pour le formulaire de mise à jour de module
interface UpdateModuleForm {
  title: string;
  description: string;
  order: number;
  content: string;
}

// Interface pour le formulaire de création de ressource
interface CreateResourceForm {
  title: string;
  description: string;
  file: File | null;
  order: number;
  moduleId: number;
}

// Interface pour le formulaire de mise à jour de ressource
interface UpdateResourceForm {
  title: string;
  description: string;
  order: number;
  moduleId: number;
}

const AdminResources = () => {
  // États pour les modules
  const [modules, setModules] = useState<ResourceModule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  // États pour la création de module
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [createForm, setCreateForm] = useState<CreateModuleForm>({
    title: "",
    description: "",
    order: 1,
    content: "",
  });
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState("");
  const [createSuccess, setCreateSuccess] = useState(false);

  // États pour l'édition de module
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedModule, setSelectedModule] = useState<ResourceModule | null>(
    null
  );
  const [editForm, setEditForm] = useState<UpdateModuleForm>({
    title: "",
    description: "",
    order: 1,
    content: "",
  });
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState("");
  const [editSuccess, setEditSuccess] = useState(false);

  // États pour la suppression de module
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [moduleToDelete, setModuleToDelete] = useState<ResourceModule | null>(
    null
  );
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [deleteSuccess, setDeleteSuccess] = useState(false);

  // États pour affichage des ressources d'un module
  const [viewingModuleResources, setViewingModuleResources] =
    useState<ResourceModule | null>(null);
  const [resources, setResources] = useState<Resource[]>([]);
  const [loadingResources, setLoadingResources] = useState(false);
  const [resourceError, setResourceError] = useState("");

  // États pour la création de ressource
  const [isCreateResourceModalOpen, setIsCreateResourceModalOpen] =
    useState(false);
  const [createResourceForm, setCreateResourceForm] =
    useState<CreateResourceForm>({
      title: "",
      description: "",
      file: null,
      order: 1,
      moduleId: 0,
    });
  const [createResourceLoading, setCreateResourceLoading] = useState(false);
  const [createResourceError, setCreateResourceError] = useState("");
  const [createResourceSuccess, setCreateResourceSuccess] = useState(false);

  // États pour l'édition de ressource
  const [isEditResourceModalOpen, setIsEditResourceModalOpen] = useState(false);
  const [selectedResource, setSelectedResource] = useState<Resource | null>(
    null
  );
  const [editResourceForm, setEditResourceForm] = useState<UpdateResourceForm>({
    title: "",
    description: "",
    order: 1,
    moduleId: 0,
  });
  const [editResourceLoading, setEditResourceLoading] = useState(false);
  const [editResourceError, setEditResourceError] = useState("");
  const [editResourceSuccess, setEditResourceSuccess] = useState(false);

  // États pour la suppression de ressource
  const [isDeleteResourceModalOpen, setIsDeleteResourceModalOpen] =
    useState(false);
  const [resourceToDelete, setResourceToDelete] = useState<Resource | null>(
    null
  );
  const [deleteResourceLoading, setDeleteResourceLoading] = useState(false);
  const [deleteResourceError, setDeleteResourceError] = useState("");
  const [deleteResourceSuccess, setDeleteResourceSuccess] = useState(false);

  // États pour afficher les détails d'une ressource
  const [isViewResourceModalOpen, setIsViewResourceModalOpen] = useState(false);
  const [resourceToView, setResourceToView] = useState<Resource | null>(null);

  useEffect(() => {
    fetchModules();
  }, []);

  const fetchModules = async () => {
    setLoading(true);
    setError("");

    try {
      const accessToken = localStorage.getItem("accessToken");

      if (!accessToken) {
        throw new Error("Vous n'êtes pas connecté");
      }

      // Utiliser le même endpoint que Cinepro pour récupérer les modules
      const response = await fetch(API_ENDPOINTS.CINEPRO.GET_MODULES, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.message || "Erreur lors de la récupération des modules"
        );
      }

      // Tri des modules par ordre
      const sortedModules = [...data].sort((a, b) => {
        const orderA = a.order !== undefined ? a.order : 999;
        const orderB = b.order !== undefined ? b.order : 999;
        return orderA - orderB;
      });

      setModules(sortedModules);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Une erreur est survenue");
      console.error("Erreur de récupération des modules:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchResources = async (moduleId: number) => {
    setLoadingResources(true);
    setResourceError("");

    try {
      const accessToken = localStorage.getItem("accessToken");

      if (!accessToken) {
        throw new Error("Vous n'êtes pas connecté");
      }

      // Utiliser l'endpoint défini dans RESOURCES
      const response = await fetch(
        `${API_ENDPOINTS.RESOURCES.GET_RESOURCES}?moduleId=${moduleId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.message || "Erreur lors de la récupération des ressources"
        );
      }

      // Tri des ressources par ordre
      const sortedResources = [...data].sort((a, b) => {
        const orderA = a.order !== undefined ? a.order : 999;
        const orderB = b.order !== undefined ? b.order : 999;
        return orderA - orderB;
      });

      setResources(sortedResources);
    } catch (err) {
      setResourceError(
        err instanceof Error ? err.message : "Une erreur est survenue"
      );
      console.error("Erreur de récupération des ressources:", err);
    } finally {
      setLoadingResources(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    formType: "create" | "edit"
  ) => {
    const { name, value } = e.target;
    const setValue = (prev: CreateModuleForm | UpdateModuleForm) => ({
      ...prev,
      [name]: name === "order" ? Number.parseInt(value) || 0 : value,
    });

    if (formType === "create") {
      setCreateForm((prev) => setValue(prev) as CreateModuleForm);
    } else {
      setEditForm((prev) => setValue(prev) as UpdateModuleForm);
    }
  };

  const handleResourceInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setCreateResourceForm((prev) => ({
      ...prev,
      [name]: name === "order" ? Number.parseInt(value) || 0 : value,
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setCreateResourceForm((prev) => ({
        ...prev,
        file: e.target.files![0],
      }));
    }
  };

  const handleEditResourceInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setEditResourceForm((prev) => ({
      ...prev,
      [name]: name === "order" ? Number.parseInt(value) || 0 : value,
    }));
  };

  const handleCreateModule = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreateLoading(true);
    setCreateError("");
    setCreateSuccess(false);

    try {
      const accessToken = localStorage.getItem("accessToken");

      if (!accessToken) {
        throw new Error("Vous n'êtes pas connecté");
      }

      // Utiliser le même endpoint que Cinepro pour créer un module
      const response = await fetch(API_ENDPOINTS.CINEPRO.CREATE_MODULE, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(createForm),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Erreur lors de la création du module");
      }

      // Rafraîchir la liste des modules
      fetchModules();

      // Réinitialiser le formulaire et afficher un message de succès
      setCreateForm({
        title: "",
        description: "",
        order: 1,
        content: "",
      });

      setCreateSuccess(true);

      // Fermer le modal après 2 secondes
      setTimeout(() => {
        setIsCreateModalOpen(false);
        setCreateSuccess(false);
      }, 2000);
    } catch (err) {
      setCreateError(
        err instanceof Error ? err.message : "Une erreur est survenue"
      );
      console.error("Erreur de création de module:", err);
    } finally {
      setCreateLoading(false);
    }
  };

  const handleEditButtonClick = (module: ResourceModule) => {
    setSelectedModule(module);
    setEditForm({
      title: module.title,
      description: module.description,
      order: module.order || 1,
      content: module.content || "",
    });
    setIsEditModalOpen(true);
    setEditError("");
    setEditSuccess(false);
  };

  const handleViewResources = (module: ResourceModule) => {
    setViewingModuleResources(module);
    fetchResources(Number.parseInt(module.id));
  };

  const handleCreateResourceButtonClick = () => {
    if (!viewingModuleResources) return;

    setCreateResourceForm({
      title: "",
      description: "",
      file: null,
      order: resources.length + 1,
      moduleId: Number.parseInt(viewingModuleResources.id),
    });

    setIsCreateResourceModalOpen(true);
    setCreateResourceError("");
    setCreateResourceSuccess(false);
  };

  // Ajouter la fonction de téléchargement après les autres gestionnaires d'événements
  const handleDownloadResource = async (
    resourceId: string,
    resourceName: string
  ) => {
    try {
      const accessToken = localStorage.getItem("accessToken");

      if (!accessToken) {
        throw new Error("Vous n'êtes pas connecté");
      }

      // Afficher un indicateur de chargement si nécessaire

      // Effectuer la requête fetch avec l'en-tête Authorization
      const response = await fetch(
        API_ENDPOINTS.RESOURCES.DOWNLOAD_RESOURCE(resourceId),
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Erreur lors du téléchargement du fichier");
      }

      // Récupérer le blob de la réponse
      const blob = await response.blob();

      // Détecter l'extension à partir du type MIME
      let extension = "";
      const contentType = response.headers.get("content-type");

      if (contentType) {
        switch (contentType.toLowerCase()) {
          case "application/pdf":
            extension = ".pdf";
            break;
          case "application/msword":
          case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
            extension = ".docx";
            break;
          case "application/vnd.ms-excel":
          case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
            extension = ".xlsx";
            break;
          case "application/vnd.ms-powerpoint":
          case "application/vnd.openxmlformats-officedocument.presentationml.presentation":
            extension = ".pptx";
            break;
          case "image/jpeg":
            extension = ".jpg";
            break;
          case "image/png":
            extension = ".png";
            break;
          case "image/gif":
            extension = ".gif";
            break;
          case "text/plain":
            extension = ".txt";
            break;
          default: {
            // Si le type MIME n'est pas reconnu, essayer d'extraire l'extension du blob
            const blobType = blob.type.split("/").pop();
            if (blobType && blobType !== "octet-stream") {
              extension = `.${blobType}`;
            }
          }
        }
      }

      // Créer une URL d'objet pour le blob
      const url = window.URL.createObjectURL(blob);

      // Créer un élément <a> temporaire pour déclencher le téléchargement
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = url;

      // Essayer d'obtenir le nom du fichier depuis les en-têtes de réponse
      const contentDisposition = response.headers.get("content-disposition");
      let filename = resourceName;

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]*)"?/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        } else {
          // Si on ne peut pas extraire le nom de fichier, s'assurer qu'il a l'extension
          if (!filename.endsWith(extension) && extension) {
            filename += extension;
          }
        }
      } else {
        // Si pas de content-disposition, ajouter l'extension au nom de la ressource
        if (!filename.endsWith(extension) && extension) {
          filename += extension;
        }
      }

      a.download = filename;

      // Ajouter l'élément au DOM, cliquer dessus, puis le supprimer
      document.body.appendChild(a);
      a.click();

      // Nettoyer
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      console.log("Fichier téléchargé:", filename, "Type MIME:", contentType);
    } catch (error) {
      console.error("Erreur lors du téléchargement:", error);
      alert(
        error instanceof Error
          ? error.message
          : "Une erreur est survenue lors du téléchargement"
      );
    }
  };

  // Rendu du composant
  return (
    <div className="p-8">
      <h1 className="text-2xl font-bold">Ressources Pédagogiques</h1>
      <p className="mt-4">Gérez les ressources pédagogiques.</p>

      {/* Affichage des modules ou des ressources d'un module selon le contexte */}
      {!viewingModuleResources ? (
        // Vue des modules
        <div className="mt-8 bg-white p-6 rounded-lg shadow">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold">Modules</h2>
            <div className="flex gap-4">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Rechercher..."
                  className="pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <Search
                  className="absolute left-3 top-2.5 text-gray-400"
                  size={18}
                />
              </div>
              <button
                className="flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg"
                onClick={fetchModules}
                disabled={loading}
              >
                <RefreshCw
                  size={18}
                  className={`text-gray-600 ${loading ? "animate-spin" : ""}`}
                />
                <span>Actualiser</span>
              </button>
              <button
                className="flex items-center gap-2 px-3 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
                onClick={() => setIsCreateModalOpen(true)}
              >
                <Upload size={18} />
                <span>Créer un module</span>
              </button>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              <p className="ml-2">Chargement des modules...</p>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-md">
              {error}
            </div>
          ) : modules.length === 0 ? (
            <p className="text-center py-8 text-gray-500">
              Aucun module disponible. Créez votre premier module en cliquant
              sur "Créer un module".
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      N°
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Titre
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Description
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Date de création
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {modules
                    .filter(
                      (module) =>
                        module.title
                          .toLowerCase()
                          .includes(searchTerm.toLowerCase()) ||
                        module.description
                          .toLowerCase()
                          .includes(searchTerm.toLowerCase())
                    )
                    .map((module, index) => (
                      <tr key={module.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {index + 1}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {module.title}
                        </td>
                        <td className="px-6 py-4">
                          <div className="line-clamp-2">
                            {module.description}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {new Date(module.createdAt).toLocaleString("fr-FR", {
                            year: "numeric",
                            month: "numeric",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-3">
                            <button
                              className="text-blue-600 hover:text-blue-900 p-1 rounded-full hover:bg-blue-100 transition-colors"
                              onClick={() => handleEditButtonClick(module)}
                              title="Éditer"
                            >
                              <Edit size={18} />
                            </button>
                            <button
                              className="text-red-600 hover:text-red-900 p-1 rounded-full hover:bg-red-100 transition-colors"
                              onClick={() => {
                                setModuleToDelete(module);
                                setIsDeleteModalOpen(true);
                              }}
                              title="Supprimer"
                            >
                              <Trash2 size={18} />
                            </button>
                            <button
                              className="text-green-600 hover:text-green-900 p-1 rounded-full hover:bg-green-100 transition-colors"
                              onClick={() => handleViewResources(module)}
                              title="Voir les ressources"
                            >
                              <Eye size={18} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : (
        // Vue des ressources d'un module
        <div className="mt-8 bg-white p-6 rounded-lg shadow">
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center">
              <button
                onClick={() => setViewingModuleResources(null)}
                className="mr-3 text-gray-600 hover:text-blue-600 transition-colors"
                title="Retour aux modules"
              >
                <ArrowLeft size={18} />
              </button>
              <h2 className="text-xl font-semibold">
                Ressources du module : {viewingModuleResources.title}
              </h2>
            </div>
            <button
              className="flex items-center gap-2 px-3 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
              onClick={handleCreateResourceButtonClick}
            >
              <Upload size={18} />
              <span>Ajouter une ressource</span>
            </button>
          </div>

          {loadingResources ? (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              <p className="ml-2">Chargement des ressources...</p>
            </div>
          ) : resourceError ? (
            <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-md">
              {resourceError}
            </div>
          ) : resources.length === 0 ? (
            <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
              <File size={36} className="mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600">
                Aucune ressource n'est disponible pour ce module.
              </p>
              <p className="text-gray-500 text-sm mt-2">
                Cliquez sur "Ajouter une ressource" pour télécharger votre
                premier fichier.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      N°
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Titre
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Description
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Date d'ajout
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {resources.map((resource, index) => (
                    <tr key={resource.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {index + 1}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {resource.title}
                      </td>
                      <td className="px-6 py-4">
                        <div className="line-clamp-2">
                          {resource.description}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {new Date(resource.createdAt).toLocaleString("fr-FR", {
                          year: "numeric",
                          month: "numeric",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-3">
                          <button
                            className="text-blue-600 hover:text-blue-900 p-1 rounded-full hover:bg-blue-100 transition-colors"
                            onClick={() => {
                              // Logique pour éditer une ressource
                              setSelectedResource(resource);
                              setEditResourceForm({
                                title: resource.title,
                                description: resource.description,
                                order: resource.order || 1,
                                moduleId: resource.moduleId,
                              });
                              setIsEditResourceModalOpen(true);
                            }}
                            title="Éditer"
                          >
                            <Edit size={18} />
                          </button>
                          <button
                            className="text-red-600 hover:text-red-900 p-1 rounded-full hover:bg-red-100 transition-colors"
                            onClick={() => {
                              // Logique pour supprimer une ressource
                              setResourceToDelete(resource);
                              setIsDeleteResourceModalOpen(true);
                            }}
                            title="Supprimer"
                          >
                            <Trash2 size={18} />
                          </button>
                          <button
                            className="text-green-600 hover:text-green-900 p-1 rounded-full hover:bg-green-100 transition-colors"
                            onClick={() => {
                              setResourceToView(resource);
                              setIsViewResourceModalOpen(true);
                            }}
                            title="Voir les détails"
                          >
                            <Eye size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Modals pour les modules (création, édition, suppression) et les ressources seront ajoutés ici */}

      {/* Modal de création de module */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 w-full max-w-md relative">
            <button
              onClick={() => setIsCreateModalOpen(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
            >
              <X size={24} />
            </button>

            <h2 className="text-2xl font-bold mb-6">Créer un module</h2>

            {createSuccess && (
              <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-4">
                <p>Module créé avec succès!</p>
              </div>
            )}

            {createError && (
              <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4">
                <p>{createError}</p>
              </div>
            )}

            <form onSubmit={handleCreateModule}>
              <div className="mb-4">
                <label htmlFor="create-module-title" className="block text-gray-700 text-sm font-bold mb-2">
                  Titre
                </label>
                <input
                  id="create-module-title"
                  type="text"
                  name="title"
                  value={createForm.title}
                  onChange={(e) => handleInputChange(e, "create")}
                  className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div className="mb-4">
                <label htmlFor="create-module-description" className="block text-gray-700 text-sm font-bold mb-2">
                  Description
                </label>
                <textarea
                  id="create-module-description"
                  name="description"
                  value={createForm.description}
                  onChange={(e) => handleInputChange(e, "create")}
                  className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={4}
                  required
                />
              </div>

              <div className="mb-4">
                <label htmlFor="create-module-order" className="block text-gray-700 text-sm font-bold mb-2">
                  Ordre
                </label>
                <input
                  id="create-module-order"
                  type="number"
                  name="order"
                  value={createForm.order}
                  onChange={(e) => handleInputChange(e, "create")}
                  className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="1"
                  required
                />
              </div>

              <div className="mb-4">
                <label htmlFor="create-module-content" className="block text-gray-700 text-sm font-bold mb-2">
                  Contenu
                </label>
                <input
                  id="create-module-content"
                  type="text"
                  name="content"
                  value={createForm.content}
                  onChange={(e) => handleInputChange(e, "create")}
                  className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="1"
                  required
                />
              </div>

              <div className="flex justify-end mt-6">
                <button
                  type="button"
                  onClick={() => setIsCreateModalOpen(false)}
                  className="mr-2 px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors flex items-center gap-2"
                  disabled={createLoading}
                >
                  {createLoading && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  )}
                  Créer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal d'édition de module */}
      {isEditModalOpen && selectedModule && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 w-full max-w-md relative">
            <button
              onClick={() => setIsEditModalOpen(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
            >
              <X size={24} />
            </button>

            <h2 className="text-2xl font-bold mb-6">Modifier le module</h2>

            {editSuccess && (
              <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-4">
                <p>Module modifié avec succès!</p>
              </div>
            )}

            {editError && (
              <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4">
                <p>{editError}</p>
              </div>
            )}

            <form
              onSubmit={(e) => {
                e.preventDefault();
                // Implémenter la mise à jour du module
                if (!selectedModule) return;

                setEditLoading(true);
                setEditError("");
                setEditSuccess(false);

                // Appel API (à compléter selon votre API)
                setTimeout(() => {
                  setEditLoading(false);
                  setEditSuccess(true);
                  setTimeout(() => {
                    setIsEditModalOpen(false);
                    setEditSuccess(false);
                  }, 2000);
                }, 1000);
              }}
            >
              <div className="mb-4">
                <label htmlFor="edit-module-title" className="block text-gray-700 text-sm font-bold mb-2">
                  Titre
                </label>
                <input
                  id="edit-module-title"
                  type="text"
                  name="title"
                  value={editForm.title}
                  onChange={(e) => handleInputChange(e, "edit")}
                  className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div className="mb-4">
                <label htmlFor="edit-module-description" className="block text-gray-700 text-sm font-bold mb-2">
                  Description
                </label>
                <textarea
                  id="edit-module-description"
                  name="description"
                  value={editForm.description}
                  onChange={(e) => handleInputChange(e, "edit")}
                  className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={4}
                  required
                />
              </div>

              <div className="mb-4">
                <label htmlFor="edit-module-order" className="block text-gray-700 text-sm font-bold mb-2">
                  Ordre
                </label>
                <input
                  id="edit-module-order"
                  type="number"
                  name="order"
                  value={editForm.order}
                  onChange={(e) => handleInputChange(e, "edit")}
                  className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="1"
                  required
                />
              </div>

              <div className="flex justify-end mt-6">
                <button
                  type="button"
                  onClick={() => setIsEditModalOpen(false)}
                  className="mr-2 px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors flex items-center gap-2"
                  disabled={editLoading}
                >
                  {editLoading && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  )}
                  Mettre à jour
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de suppression de module */}
      {isDeleteModalOpen && moduleToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 w-full max-w-md relative">
            <button
              onClick={() => setIsDeleteModalOpen(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
            >
              <X size={24} />
            </button>

            <h2 className="text-2xl font-bold mb-6 flex items-center text-red-600">
              <AlertTriangle className="mr-2" size={24} />
              Supprimer le module
            </h2>

            {deleteSuccess && (
              <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-4">
                <p>Module supprimé avec succès!</p>
              </div>
            )}

            {deleteError && (
              <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4">
                <p>{deleteError}</p>
              </div>
            )}

            <p className="mb-4">
              Êtes-vous sûr de vouloir supprimer le module{" "}
              <span className="font-semibold">{moduleToDelete.title}</span> ?
            </p>
            <p className="mb-6 text-sm text-red-500">
              Cette action est irréversible et supprimera également toutes les
              ressources associées à ce module.
            </p>

            <div className="flex justify-end mt-6">
              <button
                type="button"
                onClick={() => setIsDeleteModalOpen(false)}
                className="mr-2 px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
                disabled={deleteLoading}
              >
                Annuler
              </button>
              <button
                type="button"
                className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors flex items-center gap-2"
                disabled={deleteLoading}
                onClick={() => {
                  // Implémenter la suppression du module
                  setDeleteLoading(true);
                  setDeleteError("");
                  setDeleteSuccess(false);

                  const accessToken = localStorage.getItem("accessToken");
                  if (!accessToken) {
                    setDeleteError("Vous n'êtes pas connecté");
                    setDeleteLoading(false);
                    return;
                  }

                  // Utiliser l'endpoint de Cinepro pour supprimer un module
                  fetch(
                    API_ENDPOINTS.CINEPRO.DELETE_MODULE(moduleToDelete.id),
                    {
                      method: "DELETE",
                      headers: {
                        Authorization: `Bearer ${accessToken}`,
                      },
                    }
                  )
                    .then((response) => {
                      if (!response.ok) {
                        return response.json().then((data) => {
                          throw new Error(
                            data.message ||
                              "Erreur lors de la suppression du module"
                          );
                        });
                      }
                      return response.json().catch(() => ({})); // Si le body est vide
                    })
                    .then(() => {
                      setDeleteSuccess(true);
                      // Rafraîchir la liste des modules
                      fetchModules();

                      setTimeout(() => {
                        setIsDeleteModalOpen(false);
                        setDeleteSuccess(false);
                        setModuleToDelete(null);
                      }, 2000);
                    })
                    .catch((err) => {
                      console.error(
                        "Erreur lors de la suppression du module:",
                        err
                      );
                      setDeleteError(err.message || "Une erreur est survenue");
                    })
                    .finally(() => {
                      setDeleteLoading(false);
                    });
                }}
              >
                {deleteLoading && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                )}
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de création de ressource */}
      {isCreateResourceModalOpen && viewingModuleResources && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 w-full max-w-md relative">
            <button
              onClick={() => setIsCreateResourceModalOpen(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
            >
              <X size={24} />
            </button>

            <h2 className="text-2xl font-bold mb-6">Ajouter une ressource</h2>

            {createResourceSuccess && (
              <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-4">
                <p>Ressource ajoutée avec succès!</p>
              </div>
            )}

            {createResourceError && (
              <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4">
                <p>{createResourceError}</p>
              </div>
            )}

            <form
              onSubmit={(e) => {
                e.preventDefault();
                // Implémenter l'ajout d'une ressource
                setCreateResourceLoading(true);
                setCreateResourceError("");
                setCreateResourceSuccess(false);

                const formData = new FormData();
                formData.append("title", createResourceForm.title);
                formData.append("description", createResourceForm.description);
                formData.append(
                  "moduleId",
                  createResourceForm.moduleId.toString()
                );

                if (createResourceForm.file) {
                  formData.append("file", createResourceForm.file);
                } else {
                  setCreateResourceError("Veuillez sélectionner un fichier");
                  setCreateResourceLoading(false);
                  return;
                }

                const accessToken = localStorage.getItem("accessToken");
                if (!accessToken) {
                  setCreateResourceError("Vous n'êtes pas connecté");
                  setCreateResourceLoading(false);
                  return;
                }

                // Utiliser l'endpoint défini dans RESOURCES
                fetch(API_ENDPOINTS.RESOURCES.CREATE_RESOURCE, {
                  method: "POST",
                  headers: {
                    Authorization: `Bearer ${accessToken}`,
                    // Ne pas inclure Content-Type pour FormData
                  },
                  body: formData,
                })
                  .then((response) => {
                    if (!response.ok) {
                      return response.json().then((data) => {
                        throw new Error(
                          data.message ||
                            "Erreur lors de l'ajout de la ressource"
                        );
                      });
                    }
                    return response.json();
                  })
                  .then(() => {
                    setCreateResourceSuccess(true);

                    // Rafraîchir la liste des ressources
                    if (viewingModuleResources) {
                      fetchResources(Number.parseInt(viewingModuleResources.id));
                    }

                    // Réinitialiser le formulaire
                    setCreateResourceForm({
                      title: "",
                      description: "",
                      file: null,
                      order: resources.length + 1,
                      moduleId: Number.parseInt(viewingModuleResources.id),
                    });

                    setTimeout(() => {
                      setIsCreateResourceModalOpen(false);
                      setCreateResourceSuccess(false);
                    }, 2000);
                  })
                  .catch((err) => {
                    console.error(
                      "Erreur lors de l'ajout de la ressource:",
                      err
                    );
                    setCreateResourceError(
                      err.message || "Une erreur est survenue"
                    );
                  })
                  .finally(() => {
                    setCreateResourceLoading(false);
                  });
              }}
            >
              <div className="mb-4">
                <label htmlFor="create-resource-title" className="block text-gray-700 text-sm font-bold mb-2">
                  Titre
                </label>
                <input
                  id="create-resource-title"
                  type="text"
                  name="title"
                  value={createResourceForm.title}
                  onChange={handleResourceInputChange}
                  className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div className="mb-4">
                <label htmlFor="create-resource-description" className="block text-gray-700 text-sm font-bold mb-2">
                  Description
                </label>
                <textarea
                  id="create-resource-description"
                  name="description"
                  value={createResourceForm.description}
                  onChange={handleResourceInputChange}
                  className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  required
                />
              </div>

              <div className="mb-4">
                <label htmlFor="create-resource-file" className="block text-gray-700 text-sm font-bold mb-2">
                  Fichier
                </label>
                <input
                  id="create-resource-file"
                  type="file"
                  name="file"
                  onChange={handleFileChange}
                  className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Formats supportés : PDF, DOCX, PPTX, JPG, PNG, etc.
                </p>
              </div>

              <div className="mb-4">
                <label htmlFor="create-resource-order" className="block text-gray-700 text-sm font-bold mb-2">
                  Ordre
                </label>
                <input
                  id="create-resource-order"
                  type="number"
                  name="order"
                  value={createResourceForm.order}
                  onChange={handleResourceInputChange}
                  className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="1"
                  required
                />
              </div>

              <div className="mb-4">
                <label htmlFor="create-resource-module" className="block text-gray-700 text-sm font-bold mb-2">
                  Module
                </label>
                <input
                  id="create-resource-module"
                  type="text"
                  value={viewingModuleResources.title}
                  className="w-full p-2 bg-gray-100 border border-gray-300 rounded-md text-gray-600"
                  disabled
                />
              </div>

              <div className="flex justify-end mt-6">
                <button
                  type="button"
                  onClick={() => setIsCreateResourceModalOpen(false)}
                  className="mr-2 px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors flex items-center gap-2"
                  disabled={createResourceLoading}
                >
                  {createResourceLoading && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  )}
                  Ajouter
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal d'édition de ressource */}
      {isEditResourceModalOpen && selectedResource && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 w-full max-w-md relative">
            <button
              onClick={() => setIsEditResourceModalOpen(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
            >
              <X size={24} />
            </button>

            <h2 className="text-2xl font-bold mb-6">Modifier la ressource</h2>

            {editResourceSuccess && (
              <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-4">
                <p>Ressource modifiée avec succès!</p>
              </div>
            )}

            {editResourceError && (
              <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4">
                <p>{editResourceError}</p>
              </div>
            )}

            <form
              onSubmit={(e) => {
                e.preventDefault();
                // Implémenter la mise à jour de la ressource
                setEditResourceLoading(true);
                setEditResourceError("");
                setEditResourceSuccess(false);

                const accessToken = localStorage.getItem("accessToken");
                if (!accessToken) {
                  setEditResourceError("Vous n'êtes pas connecté");
                  setEditResourceLoading(false);
                  return;
                }

                // Utiliser l'endpoint défini dans RESOURCES
                fetch(
                  API_ENDPOINTS.RESOURCES.UPDATE_RESOURCE(selectedResource.id),
                  {
                    method: "PUT",
                    headers: {
                      "Content-Type": "application/json",
                      Authorization: `Bearer ${accessToken}`,
                    },
                    body: JSON.stringify({
                      title: editResourceForm.title,
                      description: editResourceForm.description,
                      order: editResourceForm.order,
                      moduleId: editResourceForm.moduleId,
                    }),
                  }
                )
                  .then((response) => {
                    if (!response.ok) {
                      return response.json().then((data) => {
                        throw new Error(
                          data.message ||
                            "Erreur lors de la mise à jour de la ressource"
                        );
                      });
                    }
                    return response.json();
                  })
                  .then(() => {
                    setEditResourceSuccess(true);

                    // Rafraîchir la liste des ressources
                    if (viewingModuleResources) {
                      fetchResources(Number.parseInt(viewingModuleResources.id));
                    }

                    setTimeout(() => {
                      setIsEditResourceModalOpen(false);
                      setEditResourceSuccess(false);
                    }, 2000);
                  })
                  .catch((err) => {
                    console.error(
                      "Erreur lors de la mise à jour de la ressource:",
                      err
                    );
                    setEditResourceError(
                      err.message || "Une erreur est survenue"
                    );
                  })
                  .finally(() => {
                    setEditResourceLoading(false);
                  });
              }}
            >
              <div className="mb-4">
                <label htmlFor="edit-resource-title" className="block text-gray-700 text-sm font-bold mb-2">
                  Titre
                </label>
                <input
                  id="edit-resource-title"
                  type="text"
                  name="title"
                  value={editResourceForm.title}
                  onChange={handleEditResourceInputChange}
                  className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div className="mb-4">
                <label htmlFor="edit-resource-description" className="block text-gray-700 text-sm font-bold mb-2">
                  Description
                </label>
                <textarea
                  id="edit-resource-description"
                  name="description"
                  value={editResourceForm.description}
                  onChange={handleEditResourceInputChange}
                  className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  required
                />
              </div>

              <div className="mb-4">
                <label htmlFor="edit-resource-file" className="block text-gray-700 text-sm font-bold mb-2">
                  Fichier actuel
                </label>
                <div id="edit-resource-file" className="flex items-center p-2 bg-gray-100 rounded-md">
                  <File size={18} className="text-blue-500 mr-2" />
                  <button
                    onClick={() =>
                      handleDownloadResource(
                        selectedResource.id,
                        selectedResource.title
                      )
                    }
                    className="text-blue-500 hover:underline text-sm truncate cursor-pointer"
                  >
                    {selectedResource.title}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Pour remplacer le fichier, veuillez supprimer cette ressource
                  et en créer une nouvelle.
                </p>
              </div>

              <div className="mb-4">
                <label htmlFor="edit-resource-order" className="block text-gray-700 text-sm font-bold mb-2">
                  Ordre
                </label>
                <input
                  id="edit-resource-order"
                  type="number"
                  name="order"
                  value={editResourceForm.order}
                  onChange={handleEditResourceInputChange}
                  className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="1"
                  required
                />
              </div>

              <div className="flex justify-end mt-6">
                <button
                  type="button"
                  onClick={() => setIsEditResourceModalOpen(false)}
                  className="mr-2 px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors flex items-center gap-2"
                  disabled={editResourceLoading}
                >
                  {editResourceLoading && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  )}
                  Mettre à jour
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de suppression de ressource */}
      {isDeleteResourceModalOpen && resourceToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 w-full max-w-md relative">
            <button
              onClick={() => setIsDeleteResourceModalOpen(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
            >
              <X size={24} />
            </button>

            <h2 className="text-2xl font-bold mb-6 flex items-center text-red-600">
              <AlertTriangle className="mr-2" size={24} />
              Supprimer la ressource
            </h2>

            {deleteResourceSuccess && (
              <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-4">
                <p>Ressource supprimée avec succès!</p>
              </div>
            )}

            {deleteResourceError && (
              <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4">
                <p>{deleteResourceError}</p>
              </div>
            )}

            <p className="mb-4">
              Êtes-vous sûr de vouloir supprimer la ressource{" "}
              <span className="font-semibold">{resourceToDelete.title}</span> ?
            </p>
            <p className="mb-6 text-sm text-red-500">
              Cette action est irréversible et supprimera définitivement le
              fichier associé.
            </p>

            <div className="flex justify-end mt-6">
              <button
                type="button"
                onClick={() => setIsDeleteResourceModalOpen(false)}
                className="mr-2 px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
                disabled={deleteResourceLoading}
              >
                Annuler
              </button>
              <button
                type="button"
                className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors flex items-center gap-2"
                disabled={deleteResourceLoading}
                onClick={() => {
                  // Implémenter la suppression de la ressource
                  setDeleteResourceLoading(true);
                  setDeleteResourceError("");
                  setDeleteResourceSuccess(false);

                  const accessToken = localStorage.getItem("accessToken");
                  if (!accessToken) {
                    setDeleteResourceError("Vous n'êtes pas connecté");
                    setDeleteResourceLoading(false);
                    return;
                  }

                  // Utiliser l'endpoint défini dans RESOURCES
                  fetch(
                    API_ENDPOINTS.RESOURCES.DELETE_RESOURCE(
                      resourceToDelete.id
                    ),
                    {
                      method: "DELETE",
                      headers: {
                        Authorization: `Bearer ${accessToken}`,
                      },
                    }
                  )
                    .then((response) => {
                      if (!response.ok) {
                        return response.json().then((data) => {
                          throw new Error(
                            data.message ||
                              "Erreur lors de la suppression de la ressource"
                          );
                        });
                      }
                      return response.json().catch(() => ({})); // Si le body est vide
                    })
                    .then(() => {
                      setDeleteResourceSuccess(true);

                      // Rafraîchir la liste des ressources
                      if (viewingModuleResources) {
                        fetchResources(Number.parseInt(viewingModuleResources.id));
                      }

                      setTimeout(() => {
                        setIsDeleteResourceModalOpen(false);
                        setDeleteResourceSuccess(false);
                        setResourceToDelete(null);
                      }, 2000);
                    })
                    .catch((err) => {
                      console.error(
                        "Erreur lors de la suppression de la ressource:",
                        err
                      );
                      setDeleteResourceError(
                        err.message || "Une erreur est survenue"
                      );
                    })
                    .finally(() => {
                      setDeleteResourceLoading(false);
                    });
                }}
              >
                {deleteResourceLoading && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                )}
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de visualisation des détails d'une ressource */}
      {isViewResourceModalOpen && resourceToView && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 w-full max-w-md relative">
            <button
              onClick={() => setIsViewResourceModalOpen(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
            >
              <X size={24} />
            </button>

            <h2 className="text-2xl font-bold mb-4">{resourceToView.title}</h2>

            <div className="mb-4">
              <p className="text-sm text-gray-500">
                Ajouté le{" "}
                {new Date(resourceToView.createdAt).toLocaleString("fr-FR", {
                  year: "numeric",
                  month: "numeric",
                  day: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </p>
            </div>

            <div className="mb-4">
              <h3 className="text-lg font-semibold mb-2">Description</h3>
              <p className="text-gray-700">{resourceToView.description}</p>
            </div>

            <div className="mb-6 bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold mb-2">Fichier</h3>
              <div className="flex items-center">
                <File size={20} className="text-blue-500 mr-2" />
                <button
                  onClick={() =>
                    handleDownloadResource(
                      resourceToView.id,
                      resourceToView.title
                    )
                  }
                  className="text-blue-500 hover:underline text-sm truncate cursor-pointer"
                >
                  {resourceToView.title}
                </button>
              </div>
              {resourceToView.fileSize && (
                <p className="text-sm text-gray-500 mt-1">
                  Taille : {Math.round(resourceToView.fileSize / 1024)} Ko
                </p>
              )}
              <div className="mt-4 flex justify-end">
                <button
                  onClick={() =>
                    handleDownloadResource(
                      resourceToView.id,
                      resourceToView.title
                    )
                  }
                  className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors flex items-center gap-2"
                >
                  Télécharger le fichier
                </button>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => setIsViewResourceModalOpen(false)}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminResources;
